import { BaseService } from './common/baseService';
import { supabase } from "@/integrations/supabase/client";
import type { Activity } from './common/types';

export interface CreateActivityData {
  type: string;
  subject: string;
  description?: string;
  activity_date: string;
  duration_minutes?: number;
  client_id?: string;
  proposal_id?: string;
  project_id?: string;
  person_ids?: string[]; // Changed to support multiple people
  initiative_id?: string;
  assigned_to?: string;
  response_status?: string;
  status?: string;
  initiative_code?: string;
  email_message_id?: string;
  done_at?: string;
}

export interface UpdateActivityData extends Partial<CreateActivityData> {
  id: string;
}

class ActivitiesService extends BaseService {
  constructor() {
    super('activities');
  }

  async getActivities(): Promise<Activity[]> {
    return this.executeQuery(async () => {
      // First get activities with basic joins (no foreign key dependencies for profiles)
      const { data: activities, error } = await supabase.from('activities').select(`
        *,
        clients:client_id(name, id),
        proposals:proposal_id(title, id),
        projects:project_id(name, id),
        initiatives:initiative_id(id, name),
        email_messages:email_message_id(from_addr, to_addrs, cc_addrs, message_id),
        activity_people(person_id)
      `).order('activity_date', { ascending: false });
      
      if (error) {
        throw error;
      }

      // Auto-link people to email activities based on email matches
      if (activities) {
        await this.autoLinkPeopleToEmailActivities(activities);
      }
      
      // Get profile data separately to avoid foreign key issues
      let profilesData: any[] = [];
      if (activities && activities.length > 0) {
        const profileIds = new Set<string>();
        activities.forEach(activity => {
          if (activity.created_by) profileIds.add(activity.created_by);
          if (activity.assigned_to) profileIds.add(activity.assigned_to);
        });
        
        if (profileIds.size > 0) {
          const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, full_name, profile_image_url')
            .in('id', Array.from(profileIds));
          
          if (!profilesError && profiles) {
            profilesData = profiles;
          }
        }
      }
      
      // Get all unique person IDs from both activity_people and legacy person_id
      const allPersonIds = new Set<string>();
      activities?.forEach(activity => {
        // New junction table data
        activity.activity_people?.forEach((ap: any) => {
          if (ap.person_id) allPersonIds.add(ap.person_id);
        });
        // Legacy person_id field for backward compatibility
        if (activity.person_id) {
          allPersonIds.add(activity.person_id);
        }
      });
      
      // Fetch people data if we have person IDs
      let peopleData: any[] = [];
      if (allPersonIds.size > 0) {
        const { data: people, error: peopleError } = await supabase
          .rpc('get_people_secure');
        
        if (!peopleError && people) {
          peopleData = people;
        }
      }
      
      // Merge all data with activities
      const activitiesWithPeople = activities?.map(activity => {
        // Add profile data
        const creator = activity.created_by ? profilesData.find(p => p.id === activity.created_by) : null;
        const assignee = activity.assigned_to ? profilesData.find(p => p.id === activity.assigned_to) : null;
        
        // Add people data
        const peopleIds = new Set<string>();
        
        // Collect person IDs from junction table
        if (activity.activity_people && activity.activity_people.length > 0) {
          activity.activity_people.forEach((ap: any) => {
            if (ap.person_id) peopleIds.add(ap.person_id);
          });
        }
        
        // Collect person ID from legacy field for backward compatibility
        if (activity.person_id) {
          peopleIds.add(activity.person_id);
        }
        
        const people = Array.from(peopleIds)
          .map(personId => peopleData.find(p => p.id === personId))
          .filter(Boolean)
          .map((person: any) => ({
            id: person.id,
            first_name: person.first_name,
            last_name: person.last_name,
            email: person.email
          }));
          
        return {
          ...activity,
          creator,
          assignee,
          people: people.length > 0 ? people : undefined
        };
      }) || [];
      
      return { data: activitiesWithPeople as any[], error: null };
    });
  }

  async getActivityById(id: string): Promise<Activity | null> {
    return this.executeQuery(async () => {
      return await supabase
        .from('activities')
        .select(`
          *,
          clients:client_id(name, id),
          proposals:proposal_id(title, id),
          projects:project_id(name, id),
          email_messages:email_message_id(*)
        `)
        .eq('id', id)
        .maybeSingle();
    });
  }

  async createActivity(activityData: CreateActivityData): Promise<Activity> {
    return this.executeQuery(async () => {
      const { person_ids, ...activityDataWithoutPeople } = activityData;
      
      // Create the activity first
      const { data: activity, error } = await supabase
        .from('activities')
        .insert([activityDataWithoutPeople])
        .select(`
          *,
          clients:client_id(name, id),
          proposals:proposal_id(title, id),
          projects:project_id(name, id)
        `)
        .single();

      if (error) throw error;

      // If there are people to link, insert them into activity_people
      if (person_ids && person_ids.length > 0) {
        const activityPeopleData = person_ids.map(person_id => ({
          activity_id: activity.id,
          person_id
        }));

        const { error: peopleError } = await supabase
          .from('activity_people')
          .insert(activityPeopleData);

        if (peopleError) {
          // If linking people fails, we should still return the activity
          console.error('Error linking people to activity:', peopleError);
        }
      }

      return { data: activity as unknown as Activity, error: null };
    });
  }

  async updateActivity({ id, ...updateData }: UpdateActivityData): Promise<Activity> {
    return this.executeQuery(async () => {
      const { person_ids, ...activityDataWithoutPeople } = updateData;
      
      // Update the activity
      const { data: activity, error } = await supabase
        .from('activities')
        .update(activityDataWithoutPeople)
        .eq('id', id)
        .select(`
          *,
          clients:client_id(name, id),
          proposals:proposal_id(title, id),
          projects:project_id(name, id)
        `)
        .single();

      if (error) {
        throw error;
      }

      // Handle people updates if person_ids is provided
      if (person_ids !== undefined) {
        // First, remove all existing people for this activity
        const { error: deleteError } = await supabase
          .from('activity_people')
          .delete()
          .eq('activity_id', id);

        if (deleteError) {
          console.error('Error deleting existing people:', deleteError);
        }

        // Then add the new people if any
        if (person_ids.length > 0) {
          const activityPeopleData = person_ids.map(person_id => ({
            activity_id: id,
            person_id
          }));

          const { error: peopleError } = await supabase
            .from('activity_people')
            .insert(activityPeopleData);

          if (peopleError) {
            console.error('Error linking people to activity:', peopleError);
          }
        }
      }

      return { data: activity as unknown as Activity, error: null };
    });
  }

  async deleteActivity(id: string): Promise<void> {
    return this.delete(id);
  }

  async getActivitiesByType(type: string): Promise<Activity[]> {
    return this.queryTable(() => 
      supabase.from('activities').select(`
        *,
        clients:client_id(name, id),
        proposals:proposal_id(title, id),
        projects:project_id(name, id)
      `).eq('type', type).order('activity_date', { ascending: false })
    );
  }

  async getActivitiesByClient(clientId: string): Promise<Activity[]> {
    return this.queryTable(() => 
      supabase.from('activities').select(`
        *,
        clients:client_id(name, id),
        proposals:proposal_id(title, id),
        projects:project_id(name, id)
      `).eq('client_id', clientId).order('activity_date', { ascending: false })
    );
  }

  async getActivitiesByDateRange(startDate: string, endDate: string): Promise<Activity[]> {
    return this.queryTable(() => 
      supabase.from('activities').select(`
        *,
        clients:client_id(name, id),
        proposals:proposal_id(title, id),
        projects:project_id(name, id)
      `).gte('activity_date', startDate)
       .lte('activity_date', endDate)
       .order('activity_date', { ascending: false })
    );
  }

  async getActivityTypes(): Promise<string[]> {
    const data = await this.queryTable(() => 
      supabase.from('activities').select('type')
    );
    
    const types = [...new Set((data as any[])?.map((item: any) => item.type).filter(Boolean))];
    return types.sort();
  }

  async getActivityMetrics(dateRange?: { start: string; end: string }) {
    return this.executeQuery(async () => {
      let query = supabase
        .from('activities')
        .select('type, duration_minutes, activity_date, status');
      
      if (dateRange) {
        query = query
          .gte('activity_date', dateRange.start)
          .lte('activity_date', dateRange.end);
      }
      
      return query.order('activity_date', { ascending: false });
    });
  }

  // Auto-link people to email activities based on email addresses
  private async autoLinkPeopleToEmailActivities(activities: any[]): Promise<void> {
    try {
      // Get all email activities that have email_messages
      const emailActivities = activities.filter(activity => 
        activity.email_messages && 
        (activity.email_messages.to_addrs?.length > 0 || activity.email_messages.cc_addrs?.length > 0)
      );

      if (emailActivities.length === 0) return;

      // Collect all unique email addresses from all activities
      const allEmailAddresses = new Set<string>();
      emailActivities.forEach(activity => {
        const recipients = [
          ...(activity.email_messages.to_addrs || []),
          ...(activity.email_messages.cc_addrs || [])
        ].filter(email => email && email.toLowerCase() !== 'log@inbound.braincandy.gr');
        
        recipients.forEach(email => allEmailAddresses.add(email.toLowerCase()));
      });

      if (allEmailAddresses.size === 0) return;

      // Find people with matching email addresses using the secure function
      const { data: matchingPeople, error: peopleError } = await supabase
        .rpc('find_people_for_recipients', {
          recipients: Array.from(allEmailAddresses)
        });

      if (peopleError || !matchingPeople) return;

      // For each email activity, link matching people
      for (const activity of emailActivities) {
        const recipients = [
          ...(activity.email_messages.to_addrs || []),
          ...(activity.email_messages.cc_addrs || [])
        ].filter(email => email && email.toLowerCase() !== 'log@inbound.braincandy.gr');

        // Find people that match this activity's recipients
        const matchingPersonIds = new Set<string>();
        recipients.forEach(email => {
          const normalizedEmail = email.toLowerCase();
          const person = matchingPeople.find(p => p.email.toLowerCase() === normalizedEmail);
          if (person) {
            matchingPersonIds.add(person.person_id);
          }
        });

        if (matchingPersonIds.size === 0) continue;

        // Get currently linked people for this activity
        const currentlyLinked = new Set(
          (activity.activity_people || []).map((ap: any) => ap.person_id)
        );

        // Only link people that aren't already linked
        const peopleToLink = Array.from(matchingPersonIds).filter(
          personId => !currentlyLinked.has(personId)
        );

        if (peopleToLink.length === 0) continue;

        // Link the new people to the activity
        const linkData = peopleToLink.map(personId => ({
          activity_id: activity.id,
          person_id: personId
        }));

        const { error: linkError } = await supabase
          .from('activity_people')
          .insert(linkData);

        if (linkError) {
          console.error('Error auto-linking people to activity:', activity.id, linkError);
        }
      }
    } catch (error) {
      console.error('Error in auto-linking people to email activities:', error);
    }
  }
}

export const activitiesService = new ActivitiesService();